import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MoveCreationModal from '../MoveCreation/MoveCreationModal';
import { acceptMove, finishMove, fetchMoves, cancelMove } from '../../store/moves';

const MoveStatus = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const moves = useSelector(state => state.moves.list || []);

  // Fetch moves when component mounts and set up periodic refresh
  useEffect(() => {
    // Initial fetch
    dispatch(fetchMoves());

    // Set up periodic refresh every minute
    const interval = setInterval(() => {
      dispatch(fetchMoves());
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [dispatch]);

  // Calculate move counts including today's moves
  const moveCounts = {
    requested: moves.filter(move => !move.in_progress && !move.finish_time && !move.is_cancel).length,
    inProgress: moves.filter(move => move.in_progress && !move.finish_time && !move.is_cancel).length,
    finished: moves.filter(move => move.finish_time && !move.is_cancel).length,
    cancelled: moves.filter(move => move.is_cancel).length,
    // Calculate daily counts from moves that were finished/cancelled today
    dailyFinished: moves.filter(move => {
      if (!move.finish_time || move.is_cancel) return false;
      const finishDate = new Date(move.finish_time);
      const today = new Date();
      return finishDate.toDateString() === today.toDateString();
    }).length,
    dailyCancelled: moves.filter(move => {
      if (!move.is_cancel) return false;
      const cancelDate = new Date(move.cancel_time); // Assuming we have cancel_time in the move object
      const today = new Date();
      return cancelDate.toDateString() === today.toDateString();
    }).length
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status === selectedStatus ? null : status);
  };

  const handleAcceptMove = async (moveId) => {
    try {
      const updatedMove = await dispatch(acceptMove(moveId));
      if (updatedMove) {
        dispatch(fetchMoves());
      }
    } catch (error) {
      console.error('Failed to accept move:', error);
    }
  };

  const handleFinishMove = async (moveId) => {
    try {
      const updatedMove = await dispatch(finishMove(moveId));
      if (updatedMove) {
        dispatch(fetchMoves());
      }
    } catch (error) {
      console.error('Failed to finish move:', error);
    }
  };

  const handleCancelMove = async (moveId) => {
    try {
      const updatedMove = await dispatch(cancelMove(moveId));
      if (updatedMove) {
        dispatch(fetchMoves());
      }
    } catch (error) {
      console.error('Failed to cancel move:', error);
    }
  };

  const StatusCard = ({ title, count, color, status, dailyCount }) => {
    const filteredMoves = moves.filter(move => {
      switch (status) {
        case 'requested':
          return !move.in_progress && !move.finish_time && !move.is_cancel;
        case 'inProgress':
          return move.in_progress && !move.finish_time && !move.is_cancel;
        case 'finished':
          return move.finish_time && !move.is_cancel;
        case 'cancelled':
          return move.is_cancel;
        default:
          return false;
      }
    });

    return (
      <div className="flex-1">
        <div
          onClick={() => handleStatusClick(status)}
          className={`p-6 rounded-lg cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${color} mb-4`}
        >
          {dailyCount !== undefined ? (
            <div className="relative">
              <div className="text-2xl font-bold mb-1">{dailyCount}</div>
              <div className="text-md font-medium">{title}</div>
              <div className="absolute top-0 right-0 text-md text-gray-600 font-medium">
                Total: {count}
              </div>
            </div>
          ) : (
            <>
              <div className="text-2xl font-bold mb-1">{count}</div>
              <div className="text-md font-medium">{title}</div>
            </>
          )}
        </div>

        {selectedStatus === status && (
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h3 className="text-lg font-semibold mb-4">
              {title} Details
            </h3>
            <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
              {filteredMoves.map(move => (
                <div key={move.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">
                      Move #{move.id} - {move.move_details.type}
                    </span>
                    <div className="flex space-x-2">
                      {status === 'requested' && (
                        <>
                          <button
                            onClick={() => handleAcceptMove(move.id)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleCancelMove(move.id)}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {status === 'inProgress' && (
                        <>
                          <button
                            onClick={() => handleFinishMove(move.id)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Finish
                          </button>
                          <button
                            onClick={() => handleCancelMove(move.id)}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {move.move_details.moves.map((m, index) => (
                      <div key={index} className="mb-1">
                        Move {index + 1}: {m.from} → {m.to}
                        {m.trailer && (
                          <span className="ml-2 text-gray-500">
                            (Trailer: {m.trailer})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Requested: {new Date(move.request_time).toLocaleString()}
                    {status === 'finished' && (
                      <>
                        <div className="mt-1">
                          Finished: {new Date(move.finish_time).toLocaleString()}
                        </div>
                        {move.elapse_time && (
                          <div className="mt-1">
                            Duration: {move.elapse_time}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Move Status</h2>
          <button
            onClick={handleOpenModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <svg className="h-8 w-10 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Move
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatusCard
            title="Requested Moves"
            count={moveCounts.requested}
            color="bg-yellow-50 hover:bg-yellow-100"
            status="requested"
          />
          <StatusCard
            title="In Progress"
            count={moveCounts.inProgress}
            color="bg-green-50 hover:bg-green-100"
            status="inProgress"
          />
          <StatusCard
            title="Finished"
            count={moveCounts.finished}
            color="bg-blue-50 hover:bg-blue-100"
            status="finished"
            dailyCount={moveCounts.dailyFinished}
          />
          <StatusCard
            title="Cancelled"
            count={moveCounts.cancelled}
            color="bg-red-50 hover:bg-red-100"
            status="cancelled"
            dailyCount={moveCounts.dailyCancelled}
          />
        </div>
      </div>

      <MoveCreationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default MoveStatus;
